import { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { InfoIcon, Layers, Package } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { IModelGroup, IPostAttachment } from '@/redux/post/post.d';
import {
  IProjectGroupProject,
  IProjectGroupProjectLight,
} from '@/redux/projectGroup/projectGroup.d';
import {
  filterAttachmentsForCarousel,
  resolveToolSpecs,
} from '@/redux/post/post.utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import { BitGridCard } from '@/components/BitDetailDialog';

interface GroupPartsDisplayProps {
  aggregatedModelGroups: IModelGroup[];
  projects: (IProjectGroupProject | IProjectGroupProjectLight)[];
  totalPartsCount: number;
  modelIndex: number;
  setModelIndex: Dispatch<SetStateAction<number>>;
  carouselAttachments: IPostAttachment[];
}

function GroupPartsDisplay({
  aggregatedModelGroups,
  projects,
  totalPartsCount,
  modelIndex,
  setModelIndex,
  carouselAttachments,
}: GroupPartsDisplayProps) {
  const [showRelatedTools, setShowRelatedTools] = useState(false);

  const allToolData = useMemo(
    () => aggregatedModelGroups?.flatMap((group) => group.toolData) || [],
    [aggregatedModelGroups]
  );

  const attachmentMap = useMemo(() => {
    const map = new Map<
      string,
      { attachment: IPostAttachment; projectTitle: string }
    >();
    projects.forEach((project) => {
      project.attachments?.forEach((attachment) => {
        map.set(attachment._id, { attachment, projectTitle: project.title });
      });
    });
    return map;
  }, [projects]);

  const findToolGroup = (toolId: string) =>
    aggregatedModelGroups?.find((group) =>
      group.toolData.some((t) => t._id === toolId)
    );

  const findAttachmentForTool = (modelId: string, toolGroup?: IModelGroup) => {
    const directInfo = attachmentMap.get(modelId);
    if (directInfo) return directInfo;
    if (toolGroup) {
      const matchedId = toolGroup.modelIds.find((id) => attachmentMap.has(id));
      if (matchedId) return attachmentMap.get(matchedId);
    }
    return null;
  };

  const [filteredToolData, setFilteredToolData] = useState(allToolData);

  // Branchy by nature: model filter toggle + non-model guard + filter logic
  // fallow-ignore-next-line complexity
  useEffect(() => {
    const models = filterAttachmentsForCarousel(carouselAttachments);
    if (models.length === 0) {
      setFilteredToolData(allToolData);
      return;
    }
    const currentAttachment = models[modelIndex];
    if (currentAttachment?.resourceType !== AttachmentType.model) {
      setShowRelatedTools(false);
      setFilteredToolData(allToolData);
      return;
    }
    const filtered = showRelatedTools
      ? allToolData.filter((tool) => tool.modelId === currentAttachment._id)
      : allToolData;
    setFilteredToolData(filtered);
  }, [modelIndex, allToolData, carouselAttachments, showRelatedTools]);

  if (!allToolData.length) return null;

  return (
    <Card className="my-5">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex gap-2 items-center">
          <Layers className="h-5 w-5" />
          <CardTitle>Parts &amp; Tools Data</CardTitle>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Package size={16} />
          <span>{totalPartsCount} total parts</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-end space-x-2 mb-4">
          <span className="text-sm text-muted-foreground flex gap-2 items-center">
            <InfoIcon size="18px" />
            Filter based on selected model
          </span>
          <Switch
            checked={showRelatedTools}
            onCheckedChange={setShowRelatedTools}
            disabled={
              filterAttachmentsForCarousel(carouselAttachments)[modelIndex]
                ?.resourceType !== AttachmentType.model
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Branchy by nature: attachment lookup + model matching + view-model wiring */}
          {/* fallow-ignore-next-line complexity */}
          {filteredToolData.map((tool, index) => {
            const toolGroup = findToolGroup(tool._id);
            const attachmentInfo = findAttachmentForTool(
              tool.modelId,
              toolGroup
            );
            const specs = resolveToolSpecs(tool, tool.bit);
            const speedCaption = specs.spindleSpeed
              ? `${specs.spindleSpeed} RPM`
              : undefined;

            const models = filterAttachmentsForCarousel(carouselAttachments);
            let matchedAttachment = models.find((a) => a._id === tool.modelId);
            if (!matchedAttachment && toolGroup) {
              matchedAttachment = models.find((a) =>
                toolGroup.modelIds.includes(a._id)
              );
            }

            const onViewModel = matchedAttachment
              ? () => {
                  const idx = models.findIndex(
                    (a) => a._id === matchedAttachment?._id
                  );
                  setModelIndex(idx);
                }
              : undefined;

            return (
              <div key={tool._id} className="relative">
                <BitGridCard
                  bit={tool.bit}
                  fileName={attachmentInfo?.attachment.fileName}
                  spindleSpeed={speedCaption}
                  onViewModel={onViewModel}
                  cardNumber={index + 1}
                />
                {attachmentInfo?.projectTitle && (
                  <Badge className="absolute top-2 left-2 text-[10px] px-1.5 py-0 leading-relaxed bg-gray-900 text-white hover:bg-gray-900">
                    {attachmentInfo.projectTitle}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default GroupPartsDisplay;
